import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { Chapter, EASE, FadeUp } from "@/components/Reveal";
import { useLanguage } from "@/i18n/LanguageContext";

const VALUES = [50, 3800];

const Counter = ({ to }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 2.2,
      ease: EASE,
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to]);

  return <span ref={ref}>{val.toLocaleString("en-US")}</span>;
};

export default function Impact() {
  const { t } = useLanguage();
  const stats = t("impact.stats");

  return (
    <section className="py-28 md:py-40" data-testid="impact-section">
      <div className="mx-auto max-w-[1440px] px-6 md:px-12">
        <Chapter number="09" title={t("chapters.impact")} />
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12 lg:gap-8">
          <FadeUp className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-4" data-testid="impact-counter">
              {stats.map((s, i) => (
                <div
                  key={s.label}
                  className="rounded-2xl border border-white/10 bg-[#111111] p-6 transition-colors duration-500 hover:border-[#F97316]/50 md:p-7"
                  data-testid={`impact-stat-${i + 1}`}
                >
                  <p className="font-syne text-3xl font-extrabold tracking-tight md:text-4xl">
                    {i < 2 ? (
                      <>
                        <Counter to={VALUES[i]} />
                        <span className="text-[#F97316]">+</span>
                      </>
                    ) : (
                      s.text
                    )}
                  </p>
                  <p className="mt-3 font-spacemono text-[9px] uppercase tracking-[0.2em] text-white/40">{s.label}</p>
                </div>
              ))}
            </div>
            <p className="mt-7 max-w-md text-sm leading-relaxed text-white/55 md:text-base">{t("impact.subline")}</p>
          </FadeUp>
          <div className="lg:col-span-7">
            <FadeUp>
              <p className="mb-6 font-spacemono text-[10px] uppercase tracking-[0.3em] text-white/35">
                {t("impact.formatsLabel")}
              </p>
            </FadeUp>
            <div className="grid grid-cols-1 gap-x-10 sm:grid-cols-2" data-testid="impact-categories">
              {t("impact.categories").map((c, i) => (
                <FadeUp key={c} delay={i * 0.04}>
                  <div
                    className="group flex items-center gap-4 border-t border-white/10 py-4 transition-[padding] duration-300 hover:pl-2"
                    data-testid={`impact-category-${i + 1}`}
                  >
                    <span className="font-spacemono text-[10px] text-white/25 transition-colors duration-300 group-hover:text-[#F97316]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-syne text-sm font-bold uppercase tracking-wide text-white/80 transition-colors duration-300 group-hover:text-[#F97316] md:text-base">
                      {c}
                    </span>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
